import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  HStack,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiEye } from 'react-icons/fi';
import { tenantHeaders } from '../tenant';

const API_BASE = '/api/v1';

function Contacts() {
  const toast = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ email: '', first_name: '', last_name: '', phone: '', sms: '', external_id: '', tags: '' });
  const [viewingContact, setViewingContact] = useState(null);
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [contactToDelete, setContactToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    ...tenantHeaders(),
  });

  const fetchContacts = () => {
    fetch(`${API_BASE}/contacts/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setContacts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const openAddContact = () => {
    setEditingContact(null);
    setContactForm({ email: '', first_name: '', last_name: '', phone: '', sms: '', external_id: '', tags: '' });
    onModalOpen();
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      email: contact.email || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      phone: contact.phone || '',
      sms: contact.sms || '',
      external_id: contact.external_id || '',
      tags: contact.tags || '',
    });
    onModalOpen();
  };

  const openViewContact = (contact) => {
    fetch(`${API_BASE}/contacts/${contact.id}/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setViewingContact(data);
        onViewOpen();
      });
  };

  const handleSaveContact = async () => {
    const method = editingContact ? 'PUT' : 'POST';
    const url = editingContact ? `${API_BASE}/contacts/${editingContact.id}/` : `${API_BASE}/contacts/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(contactForm),
      });
      const data = await res.json();
      if (res.ok) {
        fetchContacts();
        toast({ title: editingContact ? 'Contact updated' : 'Contact created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving contact', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving contact', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (contact) => {
    setContactToDelete(contact);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/contacts/${contactToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchContacts();
        toast({ title: 'Contact deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting contact', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting contact', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setContactToDelete(null);
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Contacts</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddContact}>
          Add Contact
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Phone</Th>
              <Th>External ID</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {contacts.map(contact => (
              <Tr key={contact.id}>
                <Td fontWeight="medium">{contact.email}</Td>
                <Td>{contact.first_name} {contact.last_name}</Td>
                <Td>{contact.phone || '-'}</Td>
                <Td><Text fontSize="sm" fontFamily="mono" noOfLines={1}>{contact.external_id || '-'}</Text></Td>
                <Td>{new Date(contact.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEye />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openViewContact(contact)}
                      aria-label="View"
                    />
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditContact(contact)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(contact)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {contacts.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No contacts found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingContact ? 'Edit Contact' : 'Add Contact'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Email</FormLabel>
              <Input
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                placeholder="contact@example.com"
              />
            </FormControl>
            <HStack mb={4}>
              <FormControl>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                  placeholder="First Name"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                  placeholder="Last Name"
                />
              </FormControl>
            </HStack>
            <HStack mb={4}>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+44..."
                />
              </FormControl>
              <FormControl>
                <FormLabel>SMS</FormLabel>
                <Input
                  value={contactForm.sms}
                  onChange={(e) => setContactForm({ ...contactForm, sms: e.target.value })}
                  placeholder="+44..."
                />
              </FormControl>
            </HStack>
            <FormControl mb={4}>
              <FormLabel>External ID</FormLabel>
              <Input
                value={contactForm.external_id}
                onChange={(e) => setContactForm({ ...contactForm, external_id: e.target.value })}
                placeholder="External identifier"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Tags (JSON)</FormLabel>
              <Textarea
                value={contactForm.tags}
                onChange={(e) => setContactForm({ ...contactForm, tags: e.target.value })}
                placeholder='["tag1", "tag2"]'
                rows={2}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveContact}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Contact Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingContact && (
              <>
                <Text><strong>Email:</strong> {viewingContact.email}</Text>
                <Text><strong>Name:</strong> {viewingContact.first_name} {viewingContact.last_name}</Text>
                <Text><strong>Phone:</strong> {viewingContact.phone || '-'}</Text>
                <Text><strong>SMS:</strong> {viewingContact.sms || '-'}</Text>
                <Text><strong>External ID:</strong> {viewingContact.external_id || '-'}</Text>
                <Text><strong>Tags:</strong> {viewingContact.tags}</Text>
                <Text><strong>Created:</strong> {new Date(viewingContact.created_at).toLocaleString()}</Text>
                <Text><strong>Updated:</strong> {new Date(viewingContact.updated_at).toLocaleString()}</Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete contact "{contactToDelete?.email}"?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Contacts;
